import React, { useMemo, useState } from "react";

import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  AddOutlined,
  AdminPanelSettingsOutlined,
  Clear,
  CloseOutlined,
  EditOutlined,
  GroupsOutlined,
  KeyOutlined,
  PersonAddAltOutlined,
  SearchOutlined,
  SecurityOutlined,
  SupervisorAccountOutlined,
} from "@mui/icons-material";

/* =========================================================
   COLORS
========================================================= */

const PRIMARY = "#008B8B";
const PRIMARY_LIGHT = "#00A6A6";
const PRIMARY_DARK = "#006F6F";

const TEXT = "#333333";
const BACKGROUND = "#F6F8F9";
const PAPER = "#FFFFFF";

/* =========================================================
   INITIAL USERS
========================================================= */

const initialUsers = [
  {
    id: 1,
    name: "Admin User",
    email: "admin@praneeshinfotech.com",
    mobile: "+91 98765 43210",
    role: "Super Admin",
    department: "Management",
    status: "Active",
    lastLogin: "06 Oct 2026, 10:45 AM",
  },
  {
    id: 2,
    name: "Praneesh Kumar",
    email: "praneesh@praneeshinfotech.com",
    mobile: "+91 98765 12345",
    role: "Administrator",
    department: "Management",
    status: "Active",
    lastLogin: "06 Oct 2026, 09:30 AM",
  },
  {
    id: 3,
    name: "Nandha Kumar",
    email: "nandha@praneeshinfotech.com",
    mobile: "+91 98432 56789",
    role: "Manager",
    department: "Sales",
    status: "Active",
    lastLogin: "05 Oct 2026, 06:15 PM",
  },
  {
    id: 4,
    name: "Arun Kumar",
    email: "arun@praneeshinfotech.com",
    mobile: "+91 98765 11111",
    role: "Sales Executive",
    department: "Sales",
    status: "Active",
    lastLogin: "05 Oct 2026, 04:20 PM",
  },
  {
    id: 5,
    name: "Suresh B",
    email: "suresh@praneeshinfotech.com",
    mobile: "+91 97865 22222",
    role: "Accountant",
    department: "Accounts",
    status: "Active",
    lastLogin: "04 Oct 2026, 11:10 AM",
  },
  {
    id: 6,
    name: "Karthik M",
    email: "karthik@praneeshinfotech.com",
    mobile: "+91 97915 33333",
    role: "Support Executive",
    department: "Support",
    status: "Inactive",
    lastLogin: "28 Sep 2026, 03:40 PM",
  },
  {
    id: 7,
    name: "Divya S",
    email: "divya@praneeshinfotech.com",
    mobile: "+91 98654 44444",
    role: "HR Executive",
    department: "HR",
    status: "Active",
    lastLogin: "03 Oct 2026, 10:25 AM",
  },
  {
    id: 8,
    name: "Vijay R",
    email: "vijay@praneeshinfotech.com",
    mobile: "+91 99521 55555",
    role: "Sales Executive",
    department: "Sales",
    status: "Inactive",
    lastLogin: "25 Sep 2026, 05:30 PM",
  },
];

/* =========================================================
   FORM
========================================================= */

const emptyForm = {
  name: "",
  email: "",
  mobile: "",
  role: "",
  department: "",
  password: "",
  status: "Active",
};

/* =========================================================
   OPTIONS
========================================================= */

const roleOptions = [
  "Super Admin",
  "Administrator",
  "Manager",
  "Sales Executive",
  "Accountant",
  "Support Executive",
  "HR Executive",
];

const departmentOptions = [
  "Management",
  "Sales",
  "Accounts",
  "Support",
  "HR",
  "Purchase",
  "Projects",
];

/* =========================================================
   HELPERS
========================================================= */

const getInitials = (name) => {
  if (!name) return "U";

  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

function Users() {
  /* -------------------------------------------------------
     STATE
  ------------------------------------------------------- */

  const [users, setUsers] = useState(initialUsers);

  const [search, setSearch] = useState("");

  const [roleFilter, setRoleFilter] = useState("All");

  const [statusFilter, setStatusFilter] = useState("All");

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDialog, setOpenDialog] = useState(false);

  const [editingUser, setEditingUser] = useState(null);

  const [form, setForm] = useState(emptyForm);

  /* -------------------------------------------------------
     SUMMARY
  ------------------------------------------------------- */

  const totalUsers = users.length;

  const activeUsers = users.filter(
    (user) => user.status === "Active"
  ).length;

  const inactiveUsers = users.filter(
    (user) => user.status === "Inactive"
  ).length;

  const adminUsers = users.filter(
    (user) =>
      user.role === "Super Admin" ||
      user.role === "Administrator"
  ).length;

  /* -------------------------------------------------------
     FILTER
  ------------------------------------------------------- */

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const searchText = search.toLowerCase().trim();

      const matchesSearch =
        !searchText ||
        user.name.toLowerCase().includes(searchText) ||
        user.email.toLowerCase().includes(searchText) ||
        user.mobile.toLowerCase().includes(searchText) ||
        user.role.toLowerCase().includes(searchText) ||
        user.department.toLowerCase().includes(searchText);

      const matchesRole =
        roleFilter === "All" ||
        user.role === roleFilter;

      const matchesStatus =
        statusFilter === "All" ||
        user.status === statusFilter;

      return (
        matchesSearch &&
        matchesRole &&
        matchesStatus
      );
    });
  }, [
    users,
    search,
    roleFilter,
    statusFilter,
  ]);

  /* -------------------------------------------------------
     PAGINATION
  ------------------------------------------------------- */

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* -------------------------------------------------------
     ADD USER
  ------------------------------------------------------- */

  const handleOpenAdd = () => {
    setEditingUser(null);

    setForm({
      ...emptyForm,
    });

    setOpenDialog(true);
  };

  /* -------------------------------------------------------
     EDIT USER
  ------------------------------------------------------- */

  const handleOpenEdit = (user) => {
    setEditingUser(user);

    setForm({
      name: user.name,
      email: user.email,
      mobile: user.mobile,
      role: user.role,
      department: user.department,
      password: "",
      status: user.status,
    });

    setOpenDialog(true);
  };

  /* -------------------------------------------------------
     CLOSE DIALOG
  ------------------------------------------------------- */

  const handleCloseDialog = () => {
    setOpenDialog(false);

    setEditingUser(null);

    setForm({
      ...emptyForm,
    });
  };

  /* -------------------------------------------------------
     FORM CHANGE
  ------------------------------------------------------- */

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  /* -------------------------------------------------------
     SAVE USER
  ------------------------------------------------------- */

  const handleSubmit = () => {
    if (!form.name.trim()) {
      alert("Please enter full name.");
      return;
    }

    if (!form.email.trim()) {
      alert("Please enter email address.");
      return;
    }

    if (!form.mobile.trim()) {
      alert("Please enter mobile number.");
      return;
    }

    if (!form.role) {
      alert("Please select role.");
      return;
    }

    if (!form.department) {
      alert("Please select department.");
      return;
    }

    if (!editingUser && !form.password.trim()) {
      alert("Please enter password.");
      return;
    }

    /* EDIT */
    if (editingUser) {
      setUsers((previous) =>
        previous.map((user) =>
          user.id === editingUser.id
            ? {
                ...user,
                name: form.name.trim(),
                email: form.email.trim(),
                mobile: form.mobile.trim(),
                role: form.role,
                department: form.department,
                status: form.status,
              }
            : user
        )
      );
    }

    /* ADD */
    else {
      const newUser = {
        id: Date.now(),
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        role: form.role,
        department: form.department,
        status: form.status,
        lastLogin: "Never",
      };

      setUsers((previous) => [
        newUser,
        ...previous,
      ]);
    }

    handleCloseDialog();
  };

  /* -------------------------------------------------------
     DELETE USER
  ------------------------------------------------------- */

  const handleDelete = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this user?"
    );

    if (!confirmed) {
      return;
    }

    setUsers((previous) =>
      previous.filter(
        (user) => user.id !== id
      )
    );
  };

  /* -------------------------------------------------------
     TOGGLE STATUS
  ------------------------------------------------------- */

  const handleToggleStatus = (id) => {
    setUsers((previous) =>
      previous.map((user) =>
        user.id === id
          ? {
              ...user,
              status:
                user.status === "Active"
                  ? "Inactive"
                  : "Active",
            }
          : user
      )
    );
  };

  /* -------------------------------------------------------
     PAGE CHANGE
  ------------------------------------------------------- */

  const handleChangePage = (
    event,
    newPage
  ) => {
    setPage(newPage);
  };

  /* -------------------------------------------------------
     ROWS CHANGE
  ------------------------------------------------------- */

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(event.target.value, 10)
    );

    setPage(0);
  };

  /* =======================================================
     RETURN
  ======================================================= */

  return (
    <Box
      sx={{
        minHeight: "100%",
        width: "100%",
        backgroundColor: BACKGROUND,
        px: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
        py: 2,
      }}
    >
      {/* =================================================
          PAGE HEADER
      ================================================= */}

     <Box
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    mb: 2.5,
    gap: 2,
  }}
>
  {/* LEFT - TITLE */}

  <Box>
    <Typography
      sx={{
        fontSize: {
          xs: "20px",
          sm: "24px",
        },
        fontWeight: 700,
        color: TEXT,
        lineHeight: 1.3,
      }}
    >
      Users
    </Typography>

    <Typography
      sx={{
        mt: 0.5,
        fontSize: "13px",
        color: "#667085",
      }}
    >
      Manage CRM users, roles and access permissions
    </Typography>
  </Box>

  {/* RIGHT - ADD USER */}

  <Button
    variant="contained"
    startIcon={<PersonAddAltOutlined />}
    onClick={handleOpenAdd}
    sx={{
      flexShrink: 0,
      minWidth: 120,
      height: 42,
      px: 2.2,
      borderRadius: "6px",
      backgroundColor: PRIMARY,
      color: "#FFFFFF",
      textTransform: "none",
      fontSize: "14px",
      fontWeight: 600,
      boxShadow: "none",

      "&:hover": {
        backgroundColor: PRIMARY_DARK,
        boxShadow: "none",
      },

      "@media (max-width:600px)": {
        minWidth: "auto",
        px: 1.5,
      },
    }}
  >
    Add User
  </Button>
</Box>

      {/* =================================================
          SUMMARY CARDS
      ================================================= */}

      <Grid
        container
        spacing={2}
        sx={{
          mb: 2.5,
        }}
      >
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Total Users"
            value={totalUsers}
            icon={
              <GroupsOutlined />
            }
            description="All system users"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Active Users"
            value={activeUsers}
            icon={
              <SecurityOutlined />
            }
            description="Currently active"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Inactive Users"
            value={inactiveUsers}
            icon={
              <SupervisorAccountOutlined />
            }
            description="Access disabled"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Admin Users"
            value={adminUsers}
            icon={
              <AdminPanelSettingsOutlined />
            }
            description="Admin level access"
          />
        </Grid>
      </Grid>

      {/* =================================================
          FILTERS
      ================================================= */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E1E7E8",
          borderRadius: "6px",
          backgroundColor: PAPER,
          mb: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Grid
            container
            spacing={2}
          >
            {/* SEARCH */}

            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(
                    event.target.value
                  );

                  setPage(0);
                }}
                placeholder="Search by name, email, mobile, role..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined
                        sx={{
                          fontSize: 20,
                          color: "#98A2B3",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor:
                      "#FFFFFF",
                  },
                }}
              />
            </Grid>

            {/* ROLE */}

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3,
              }}
            >
              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  Role
                </InputLabel>

                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(event) => {
                    setRoleFilter(
                      event.target.value
                    );

                    setPage(0);
                  }}
                  sx={{
                    borderRadius: "6px",
                  }}
                >
                  <MenuItem value="All">
                    All Roles
                  </MenuItem>

                  {roleOptions.map(
                    (role) => (
                      <MenuItem
                        key={role}
                        value={role}
                      >
                        {role}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* STATUS */}

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3,
              }}
            >
              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  Status
                </InputLabel>

                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => {
                    setStatusFilter(
                      event.target.value
                    );

                    setPage(0);
                  }}
                  sx={{
                    borderRadius: "6px",
                  }}
                >
                  <MenuItem value="All">
                    All Status
                  </MenuItem>

                  <MenuItem value="Active">
                    Active
                  </MenuItem>

                  <MenuItem value="Inactive">
                    Inactive
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* =================================================
          USER TABLE
      ================================================= */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E1E7E8",
          borderRadius: "6px",
          overflow: "hidden",
          backgroundColor: PAPER,
        }}
      >
        {/* TABLE HEADER */}

        <Box
          sx={{
            px: 2,
            py: 1.8,
            borderBottom:
              "1px solid #E1E7E8",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: TEXT,
                }}
              >
                User List
              </Typography>

              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#667085",
                  mt: 0.3,
                }}
              >
                {filteredUsers.length} user
                {filteredUsers.length !== 1
                  ? "s"
                  : ""}{" "}
                found
              </Typography>
            </Box>

            <Button
              size="small"
              startIcon={
                <AddOutlined />
              }
              onClick={handleOpenAdd}
              sx={{
                color: PRIMARY,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Add User
            </Button>
          </Stack>
        </Box>

        {/* TABLE */}

        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Table
            sx={{
              minWidth: 1050,
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    "#F8FAFA",
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace:
                      "nowrap",
                    fontSize: "13px",
                  }}
                >
                  User
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace:
                      "nowrap",
                    fontSize: "13px",
                  }}
                >
                  Mobile
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace:
                      "nowrap",
                    fontSize: "13px",
                  }}
                >
                  Role
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace:
                      "nowrap",
                    fontSize: "13px",
                  }}
                >
                  Department
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace:
                      "nowrap",
                    fontSize: "13px",
                  }}
                >
                  Status
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace:
                      "nowrap",
                    fontSize: "13px",
                  }}
                >
                  Last Login
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace:
                      "nowrap",
                    fontSize: "13px",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedUsers.length >
              0 ? (
                paginatedUsers.map(
                  (user) => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      {/* USER */}

                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              fontSize:
                                "13px",
                              fontWeight: 700,
                              backgroundColor:
                                PRIMARY,
                            }}
                          >
                            {getInitials(
                              user.name
                            )}
                          </Avatar>

                          <Box>
                            <Typography
                              sx={{
                                fontSize:
                                  "14px",
                                fontWeight: 600,
                                color:
                                  TEXT,
                              }}
                            >
                              {user.name}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize:
                                  "12px",
                                color:
                                  "#667085",
                                mt: 0.2,
                              }}
                            >
                              {user.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* MOBILE */}

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize:
                              "13px",
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {user.mobile}
                        </Typography>
                      </TableCell>

                      {/* ROLE */}

                      <TableCell>
                        <Chip
                          icon={
                            user.role ===
                              "Super Admin" ||
                            user.role ===
                              "Administrator" ? (
                              <AdminPanelSettingsOutlined />
                            ) : (
                              <KeyOutlined />
                            )
                          }
                          label={user.role}
                          size="small"
                          sx={{
                            borderRadius:
                              "5px",
                            fontSize:
                              "11px",
                            fontWeight: 600,
                            backgroundColor:
                              "#F1F5F5",
                            color: TEXT,
                            "& .MuiChip-icon":
                              {
                                fontSize:
                                  "15px",
                                color:
                                  PRIMARY,
                              },
                          }}
                        />
                      </TableCell>

                      {/* DEPARTMENT */}

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize:
                              "13px",
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {
                            user.department
                          }
                        </Typography>
                      </TableCell>

                      {/* STATUS */}

                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Switch
                            size="small"
                            checked={
                              user.status ===
                              "Active"
                            }
                            onChange={() =>
                              handleToggleStatus(
                                user.id
                              )
                            }
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked":
                                {
                                  color:
                                    PRIMARY,
                                },

                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor:
                                    PRIMARY,
                                },
                            }}
                          />

                          <Chip
                            label={
                              user.status
                            }
                            size="small"
                            sx={{
                              height: 24,
                              borderRadius:
                                "5px",
                              fontSize:
                                "11px",
                              fontWeight: 600,
                              backgroundColor:
                                user.status ===
                                "Active"
                                  ? "#E8F6F1"
                                  : "#FCECEC",
                              color:
                                user.status ===
                                "Active"
                                  ? "#16845B"
                                  : "#C62828",
                            }}
                          />
                        </Stack>
                      </TableCell>

                      {/* LAST LOGIN */}

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize:
                              "12px",
                            color:
                              "#667085",
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {
                            user.lastLogin
                          }
                        </Typography>
                      </TableCell>

                      {/* ACTIONS */}

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          {/* EDIT */}

                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenEdit(
                                user
                              )
                            }
                            sx={{
                              width: 32,
                              height: 32,
                              color:
                                PRIMARY,
                              borderRadius:
                                "5px",

                              "&:hover":
                                {
                                  backgroundColor:
                                    "#E8F5F5",
                                },
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>

                          {/* DELETE */}

                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDelete(
                                user.id
                              )
                            }
                            sx={{
                              width: 32,
                              height: 32,
                              color:
                                "#D32F2F",
                              borderRadius:
                                "5px",

                              "&:hover":
                                {
                                  backgroundColor:
                                    "#FCECEC",
                                },
                            }}
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                )
              ) : (
                /* EMPTY */

                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{
                      py: 8,
                    }}
                  >
                    <GroupsOutlined
                      sx={{
                        fontSize: 48,
                        color:
                          "#B8C3C5",
                        mb: 1,
                      }}
                    />

                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: TEXT,
                      }}
                    >
                      No users found
                    </Typography>

                    <Typography
                      sx={{
                        fontSize:
                          "13px",
                        color:
                          "#667085",
                        mt: 0.5,
                      }}
                    >
                      Try changing your
                      search or filter
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINATION */}

        <Divider />

        <TablePagination
          component="div"
          count={
            filteredUsers.length
          }
          page={page}
          onPageChange={
            handleChangePage
          }
          rowsPerPage={
            rowsPerPage
          }
          onRowsPerPageChange={
            handleChangeRowsPerPage
          }
          rowsPerPageOptions={[
            5,
            10,
            25,
            50,
          ]}
        />
      </Paper>

      {/* =================================================
          ADD / EDIT USER DIALOG
      ================================================= */}

      <Dialog
        open={openDialog}
        onClose={
          handleCloseDialog
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
            m: 2,
          },
        }}
      >
        {/* DIALOG HEADER */}

        <DialogTitle
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },
            py: 2.2,
            backgroundColor:
              "#FFFFFF",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontSize:
                    "19px",
                  fontWeight: 700,
                  color: TEXT,
                  lineHeight: 1.3,
                }}
              >
                {editingUser
                  ? "Edit User"
                  : "Add New User"}
              </Typography>

              <Typography
                sx={{
                  fontSize:
                    "13px",
                  color:
                    "#667085",
                  mt: 0.5,
                }}
              >
                {editingUser
                  ? "Update user information and access"
                  : "Create a new CRM user account"}
              </Typography>
            </Box>

            <IconButton
              onClick={
                handleCloseDialog
              }
              sx={{
                width: 36,
                height: 36,
                borderRadius:
                  "6px",
                color:
                  "#667085",

                "&:hover": {
                  backgroundColor:
                    "#F3F5F5",
                  color: TEXT,
                },
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        {/* DIALOG CONTENT */}

        <DialogContent
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },
            py: 3,
            backgroundColor:
              "#FFFFFF",
          }}
        >
          <Grid
            container
            spacing={2}
          >
            {/* FULL NAME */}

            <Grid
              size={{
                xs: 12,
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Full Name"
                value={form.name}
                onChange={(event) =>
                  handleChange(
                    "name",
                    event.target.value
                  )
                }
                placeholder="Enter full name"
                sx={{
                  "& .MuiOutlinedInput-root":
                    {
                      borderRadius:
                        "6px",
                    },
                }}
              />
            </Grid>

            {/* EMAIL */}

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email Address"
                value={form.email}
                onChange={(event) =>
                  handleChange(
                    "email",
                    event.target.value
                  )
                }
                placeholder="name@example.com"
                sx={{
                  "& .MuiOutlinedInput-root":
                    {
                      borderRadius:
                        "6px",
                    },
                }}
              />
            </Grid>

            {/* MOBILE */}

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Mobile Number"
                value={form.mobile}
                onChange={(event) =>
                  handleChange(
                    "mobile",
                    event.target.value
                  )
                }
                placeholder="+91 XXXXX XXXXX"
                sx={{
                  "& .MuiOutlinedInput-root":
                    {
                      borderRadius:
                        "6px",
                    },
                }}
              />
            </Grid>

            {/* ROLE */}

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  Role
                </InputLabel>

                <Select
                  value={form.role}
                  label="Role"
                  onChange={(event) =>
                    handleChange(
                      "role",
                      event.target.value
                    )
                  }
                  sx={{
                    borderRadius:
                      "6px",
                  }}
                >
                  {roleOptions.map(
                    (role) => (
                      <MenuItem
                        key={role}
                        value={role}
                      >
                        {role}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* DEPARTMENT */}

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  Department
                </InputLabel>

                <Select
                  value={
                    form.department
                  }
                  label="Department"
                  onChange={(event) =>
                    handleChange(
                      "department",
                      event.target.value
                    )
                  }
                  sx={{
                    borderRadius:
                      "6px",
                  }}
                >
                  {departmentOptions.map(
                    (department) => (
                      <MenuItem
                        key={department}
                        value={
                          department
                        }
                      >
                        {department}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* PASSWORD */}

            {!editingUser && (
              <Grid
                size={{
                  xs: 12,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Password"
                  value={
                    form.password
                  }
                  onChange={(event) =>
                    handleChange(
                      "password",
                      event.target.value
                    )
                  }
                  placeholder="Enter password"
                  sx={{
                    "& .MuiOutlinedInput-root":
                      {
                        borderRadius:
                          "6px",
                      },
                  }}
                />
              </Grid>
            )}

            {/* USER STATUS */}

            <Grid
              size={{
                xs: 12,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  border:
                    "1px solid #E1E7E8",
                  borderRadius:
                    "6px",
                  backgroundColor:
                    "#FAFCFC",
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize:
                          "14px",
                        fontWeight: 700,
                        color: TEXT,
                      }}
                    >
                      User Status
                    </Typography>

                    <Typography
                      sx={{
                        fontSize:
                          "12px",
                        color:
                          "#667085",
                        mt: 0.3,
                      }}
                    >
                      Allow this user
                      to access the
                      CRM
                    </Typography>
                  </Box>

                  <Switch
                    checked={
                      form.status ===
                      "Active"
                    }
                    onChange={(
                      event
                    ) =>
                      handleChange(
                        "status",
                        event.target
                          .checked
                          ? "Active"
                          : "Inactive"
                      )
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked":
                        {
                          color:
                            PRIMARY,
                        },

                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor:
                            PRIMARY,
                        },
                    }}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        {/* DIALOG FOOTER */}

        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },
            py: 2,
            backgroundColor:
              "#FFFFFF",
            gap: 1,
          }}
        >
          <Button
            onClick={
              handleCloseDialog
            }
            sx={{
              minWidth: 90,
              height: 40,
              borderRadius:
                "6px",
              textTransform:
                "none",
              fontWeight: 600,
              color: "#444444",
              backgroundColor:
                "#F4F7F7",

              "&:hover": {
                backgroundColor:
                  "#E9EEEE",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={
              handleSubmit
            }
            startIcon={
              editingUser ? (
                <EditOutlined />
              ) : (
                <PersonAddAltOutlined />
              )
            }
            sx={{
              minWidth: 145,
              height: 40,
              borderRadius:
                "6px",
              backgroundColor:
                PRIMARY,
              textTransform:
                "none",
              fontWeight: 600,
              boxShadow: "none",

              "&:hover": {
                backgroundColor:
                  PRIMARY_DARK,
                boxShadow: "none",
              },
            }}
          >
            {editingUser
              ? "Update User"
              : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* =========================================================
   SUMMARY CARD
========================================================= */

function SummaryCard({
  title,
  value,
  icon,
  description,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border:
          "1px solid #E1E7E8",
        borderRadius: "6px",
        backgroundColor:
          PAPER,
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography
            sx={{
              fontSize:
                "13px",
              color:
                "#667085",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize:
                "26px",
              fontWeight: 700,
              color: TEXT,
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>

          <Typography
            sx={{
              fontSize:
                "12px",
              color:
                "#667085",
              mt: 0.5,
            }}
          >
            {description}
          </Typography>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius:
              "6px",
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            backgroundColor:
              "#E8F5F5",
            color: PRIMARY,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

export default Users;